import { useState, useEffect, useRef } from 'react';
import { getConductores, getConductorById, updateConductor, deleteConductor, getConductoresValidationCount, getConductoresValidationList, assignUsuarioMovil, createConductorUsuarioMovil, createConductor } from '@helpers/api/mantenedores/directorio';
import { getEmpresas } from '@helpers/api/mantenedores/empresas';
import { getTiposFlota, getCentrosDespacho, getTMS, getTiposDocumento } from '@helpers/api/mantenedores/generales';

/**
 * Hook para manejar el mantenedor de directorio de conductores
 * Maneja paginación, filtros, live search y llamadas a la API
 */

const INITIAL_PAGE_SIZE = 10;

export const useDirectorio = (user, permisos) => {
  const [conductores, setConductores] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [searchValue, setSearchValue] = useState('');
  const [empresaId, setEmpresaId] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [selectedConductor, setSelectedConductor] = useState(null);
  const [conductorDetalle, setConductorDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  // Estados para validación de conductores
  const [validationCount, setValidationCount] = useState(0);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationData, setValidationData] = useState([]);
  const [validationTotal, setValidationTotal] = useState(0);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationPageNumber, setValidationPageNumber] = useState(1);
  const [validationPageSize, setValidationPageSize] = useState(INITIAL_PAGE_SIZE);
  const [validationSearchValue, setValidationSearchValue] = useState('');
  const [selectedUsuarioSpread, setSelectedUsuarioSpread] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  
  // Estados para datos adicionales (cache)
  const [tiposFlota, setTiposFlota] = useState([]);
  const [centrosDespacho, setCentrosDespacho] = useState([]);
  const [sistemasTMS, setSistemasTMS] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [generalesLoading, setGeneralesLoading] = useState(false);

  // Obtener empresas para el filtro
  useEffect(() => {
    async function fetchEmpresas() {
      try {
        const res = await getEmpresas(user);
        if (res.success) {
          setEmpresas(res.data);
        }
      } catch (err) {
        // ...
      }
    }
    fetchEmpresas();
  }, [user]);

  // Obtener conteo de validaciones al inicializar (SOLO UNA VEZ)
  const [validationCountFetched, setValidationCountFetched] = useState(false);
  
  useEffect(() => {
    if (user && !validationCountFetched) {
      async function fetchValidationCount() {
        try {
          const res = await getConductoresValidationCount(user);
          if (res.success) {
            setValidationCount(res.data.count);
          }
        } catch (err) {
          console.error('Error al obtener conteo de validaciones:', err);
        } finally {
          setValidationCountFetched(true);
        }
      }
      fetchValidationCount();
    }
  }, [user, validationCountFetched]);

  // Función para cargar datos generales SOLO cuando se necesiten (cuando se abre el modal)
  const fetchGeneralesData = async () => {
    if (tiposFlota.length === 0 && centrosDespacho.length === 0 && sistemasTMS.length === 0 && tiposDocumento.length === 0) {
      setGeneralesLoading(true);
      try {
        const [tiposRes, centrosRes, tmsRes, tiposDocRes] = await Promise.all([
          getTiposFlota(user, { pageSize: 100 }),
          getCentrosDespacho(user, { pageSize: 100 }),
          getTMS(user, { pageSize: 100 }),
          getTiposDocumento(user, { pageSize: 100 })
        ]);
        
        if (tiposRes.success) setTiposFlota(tiposRes.data);
        if (centrosRes.success) setCentrosDespacho(centrosRes.data);
        if (tmsRes.success) setSistemasTMS(tmsRes.data);
        if (tiposDocRes.success) setTiposDocumento(tiposDocRes.data);
      } catch (err) {
        console.error('Error al obtener datos generales:', err);
      } finally {
        setGeneralesLoading(false);
      }
    }
  };


  // Control de cancelación de requests
  const abortControllerRef = useRef();
  const lastFetchId = useRef(0);
  const validationAbortControllerRef = useRef();
  const lastValidationFetchId = useRef(0);

  // Función para refrescar datos de conductores
  const refreshConductores = () => {
    // Forzar recarga actualizando una dependencia del useEffect
    setPageNumber(current => current);
  };

  useEffect(() => {
    
    // Solo buscar si searchValue tiene 5+ caracteres o está vacío
    if (searchValue.length > 0 && searchValue.length < 5) {
      setConductores([]);
      setTotal(0);
      return;
    }

    // Cancelar request anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchId = ++lastFetchId.current;

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const params = {
          pageSize,
          pageNumber,
          searchValue: searchValue.length >= 5 ? searchValue : '',
          empresa_id: empresaId
        };
        const res = await getConductores(user, params);
        
        // Solo actualizar si es la última request
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          if (res.success) {
            setConductores(res.data);
            setTotal(res.total);
          } else {
            console.error('Error en respuesta:', res.error);
            setConductores([]);
            setTotal(0);
            setError(res.error);
          }
        }
      } catch (err) {
        if (lastFetchId.current === fetchId && !controller.signal.aborted) {
          setConductores([]);
          setTotal(0);
          setError('Error al obtener conductores');
        }
      }
      if (lastFetchId.current === fetchId && !controller.signal.aborted) {
        setLoading(false);
      }
    };
    fetchData();

    // Cleanup: cancelar si cambia dependencia
    return () => {
      controller.abort();
    };
  }, [user, pageSize, pageNumber, searchValue, empresaId]);

  // Efecto para búsqueda de validaciones
  useEffect(() => {
    if (!showValidationModal) return;

    // Solo buscar si searchValue tiene 5+ caracteres o está vacío
    if (validationSearchValue.length > 0 && validationSearchValue.length < 5) {
      setValidationData([]);
      setValidationTotal(0);
      return;
    }

    // Cancelar request anterior
    if (validationAbortControllerRef.current) {
      validationAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    validationAbortControllerRef.current = controller;
    const fetchId = ++lastValidationFetchId.current;

    setValidationLoading(true);

    const fetchValidationData = async () => {
      try {
        const params = {
          pageSize: validationPageSize,
          pageNumber: validationPageNumber,
          searchValue: validationSearchValue.length >= 5 ? validationSearchValue : ''
        };
        const res = await getConductoresValidationList(user, params);
        
        // Solo actualizar si es la última request
        if (lastValidationFetchId.current === fetchId && !controller.signal.aborted) {
          if (res.success) {
            setValidationData(res.data);
            setValidationTotal(res.total);
          } else {
            setValidationData([]);
            setValidationTotal(0);
          }
        }
      } catch (err) {
        if (lastValidationFetchId.current === fetchId && !controller.signal.aborted) {
          setValidationData([]);
          setValidationTotal(0);
        }
      }
      if (lastValidationFetchId.current === fetchId && !controller.signal.aborted) {
        setValidationLoading(false);
      }
    };
    
    fetchValidationData();

    // Cleanup: cancelar si cambia dependencia
    return () => {
      controller.abort();
    };
  }, [showValidationModal, user, validationPageSize, validationPageNumber, validationSearchValue]);

  // Live search handler (solo setea el valor, el efecto hace el resto)
  const handleSearchValue = (value) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  // Empresa filter handler
  const handleEmpresaChange = (id) => {
    setEmpresaId(id);
    setPageNumber(1);
  };

  // Paginación
  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  // Cambio de tamaño de página
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageNumber(1);
  };

  // Obtener detalle de conductor por ID
  const fetchConductorDetalle = async (id) => {
    setDetalleLoading(true);
    setConductorDetalle(null);
    try {
      const res = await getConductorById(user, id);
      if (res.success) {
        setConductorDetalle(res.data);
      } else {
        setConductorDetalle(null);
      }
    } catch (err) {
      setConductorDetalle(null);
    }
    setDetalleLoading(false);
  };

  // Actualizar conductor
  const handleUpdateConductor = async (id, data) => {
    setDetalleLoading(true);
    try {
      const res = await updateConductor(user, id, data);
      if (res.success) {
        refreshConductores();
        fetchConductorDetalle(id);
      }
      return res;
    } finally {
      setDetalleLoading(false);
    }
  };

  // Eliminar conductor
  const handleDeleteConductor = async (id) => {
    setDetalleLoading(true);
    try {
      const res = await deleteConductor(user, id);
      if (res.success) {
        refreshConductores();
        setConductorDetalle(null);
      }
      return res;
    } finally {
      setDetalleLoading(false);
    }
  };

  // Crear nuevo conductor
  const handleCreateConductor = async (conductorData) => {
    setCreateLoading(true);
    try {
      const res = await createConductor(user, conductorData);
      if (res.success) {
        refreshConductores();
        // Refrescar conteo de validaciones por si el nuevo conductor necesita validación
        const validationRes = await getConductoresValidationCount(user);
        if (validationRes.success) {
          setValidationCount(validationRes.data.count);
        }
      }
      return res;
    } catch (error) {
      console.error('Error al crear conductor:', error);
      throw error;
    } finally {
      setCreateLoading(false);
    }
  };

  // Handlers para modal de creación
  const handleOpenCreateModal = async () => {
    setShowCreateModal(true);
    // Cargar datos generales solo cuando se abre el modal
    await fetchGeneralesData();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Handlers para validaciones
  const handleOpenValidationModal = async () => {
    setShowValidationModal(true);
    setValidationPageNumber(1);
    setValidationSearchValue('');
    // Cargar datos generales solo cuando se abre el modal
    await fetchGeneralesData();
  };

  const handleCloseValidationModal = () => {
    setShowValidationModal(false);
    setSelectedUsuarioSpread(null);
    setShowEditModal(false);
  };

  const handleValidationSearchValue = (value) => {
    setValidationSearchValue(value);
    setValidationPageNumber(1);
  };

  const handleValidationPageChange = (page) => {
    setValidationPageNumber(page);
  };

  const handleValidationPageSizeChange = (size) => {
    setValidationPageSize(size);
    setValidationPageNumber(1);
  };

  const handleEditUsuarioSpread = (usuario) => {
    setSelectedUsuarioSpread(usuario);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setSelectedUsuarioSpread(null);
    setShowEditModal(false);
  };

  const handleAssignUsuarioMovil = async (usuarioMovilId, assignData) => {
    try {
      const res = await assignUsuarioMovil(user, usuarioMovilId, assignData);
      if (res.success) {
        // Actualizar conteo de validaciones
        const countRes = await getConductoresValidationCount(user);
        if (countRes.success) {
          setValidationCount(countRes.data.count);
        }
        // Refrescar lista de validaciones
        const params = {
          pageSize: validationPageSize,
          pageNumber: validationPageNumber,
          searchValue: validationSearchValue.length >= 5 ? validationSearchValue : ''
        };
        const listRes = await getConductoresValidationList(user, params);
        if (listRes.success) {
          setValidationData(listRes.data);
          setValidationTotal(listRes.total);
        }
      }
      return res;
    } catch (err) {
      console.error('Error al asignar usuario móvil:', err);
      return {
        success: false,
        error: 'Error al asignar usuario móvil'
      };
    }
  };

  // Función para crear conductor y asignar usuario móvil
  const handleCreateConductorUsuarioMovil = async (usuarioMovilId, conductorData) => {
    try {
      const res = await createConductorUsuarioMovil(user, usuarioMovilId, conductorData);
      if (res.success) {
        // Actualizar conteo de validaciones
        const countRes = await getConductoresValidationCount(user);
        if (countRes.success) {
          setValidationCount(countRes.data.count);
        }
        // Refrescar lista de validaciones
        const params = {
          pageSize: validationPageSize,
          pageNumber: validationPageNumber,
          searchValue: validationSearchValue.length >= 5 ? validationSearchValue : ''
        };
        const listRes = await getConductoresValidationList(user, params);
        if (listRes.success) {
          setValidationData(listRes.data);
          setValidationTotal(listRes.total);
        }
      }
      return res;
    } catch (err) {
      console.error('Error al crear conductor y asignar usuario móvil:', err);
      return {
        success: false,
        error: 'Error al crear conductor y asignar usuario móvil'
      };
    }
  };

  return {
    conductores,
    total,
    loading,
    error,
    pageNumber,
    pageSize,
    searchValue,
    empresaId,
    empresas,
    selectedConductor,
    setSelectedConductor,
    conductorDetalle,
    detalleLoading,
    fetchConductorDetalle,
    handleUpdateConductor,
    handleDeleteConductor,
    handleSearchValue,
    handleEmpresaChange,
    handlePageChange,
    handlePageSizeChange,
    // Creación de conductores
    showCreateModal,
    createLoading,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleCreateConductor,
    // Validaciones
    validationCount,
    showValidationModal,
    validationData,
    validationTotal,
    validationLoading,
    validationPageNumber,
    validationPageSize,
    validationSearchValue,
    selectedUsuarioSpread,
    showEditModal,
    handleOpenValidationModal,
    handleCloseValidationModal,
    handleValidationSearchValue,
    handleValidationPageChange,
    handleValidationPageSizeChange,
    handleEditUsuarioSpread,
    handleCloseEditModal,
    handleAssignUsuarioMovil,
    handleCreateConductorUsuarioMovil,
    // Datos generales
    tiposFlota,
    centrosDespacho,
    sistemasTMS,
    tiposDocumento,
    generalesLoading,
    fetchGeneralesData
  };
}
